/**
 * @file DirectoryDetailDrawer.tsx
 * @description 獲證組織名錄「詳情」Drawer（只讀）。
 *
 * 名錄資料由發證流程（syncFromCertificate）自動匯總，前端不提供編輯；
 * 此 Drawer 以分組 Descriptions 完整展示 30+ 欄位（含 TAP / TOP 類別專屬欄）。
 */
import { Descriptions, Drawer, Space, Tag, Typography } from 'antd';
import {
  DIRECTORY_CATEGORY_LABELS,
  DIRECTORY_STATUS_LABELS,
  daysSinceDate,
  formatProcessingSites,
} from '@zhcb/shared';
import { useDirectoryItem, type DirectoryItem } from '@/hooks/useCertifiedOrgDirectory';

interface Props {
  /** 名錄條目 id；null 表示關閉 */
  id: number | null;
  open: boolean;
  onClose: () => void;
}

/** 空值統一顯示為 「—」 */
const dash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : v;

/** 布林顯示：true=是 / false=否 / null=— */
const yn = (v: boolean | null | undefined) => (v == null ? '—' : v ? '是' : '否');

/**
 * 依 auditorYearBase 動態產生 Y1~Y4 年度標籤（如「2023年稽核員」）。
 * 無 yearBase 時退回「第N年稽核員」。
 */
function auditorLabel(yearBase: number | null, index: number): string {
  return yearBase ? `${yearBase + index}年稽核員` : `第${index + 1}年稽核員`;
}

export function DirectoryDetailDrawer({ id, open, onClose }: Props) {
  const { data, isLoading } = useDirectoryItem(id);
  const item: DirectoryItem | undefined = data?.item;

  return (
    <Drawer
      title={
        <Space>
          <span>名錄詳情</span>
          {item && <Tag color="blue">{DIRECTORY_CATEGORY_LABELS[item.category]}</Tag>}
          {item?.rotationWarning && <Tag color="red">連任3年</Tag>}
          {item?.pendingTermination && <Tag color="orange">待停止/結束</Tag>}
        </Space>
      }
      size="large"
      open={open}
      onClose={onClose}
      loading={isLoading}
    >
      {item && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            名錄資料由發證流程自動匯總，如需修正請聯繫系統管理員。
          </Typography.Text>

          <Descriptions title="基本" bordered size="small" column={2}>
            <Descriptions.Item label="驗證註冊號">{item.registrationNo}</Descriptions.Item>
            <Descriptions.Item label="證書編號">{dash(item.certificateNo)}</Descriptions.Item>
            <Descriptions.Item label="受評者">{item.clientName}</Descriptions.Item>
            <Descriptions.Item label="狀態">
              {DIRECTORY_STATUS_LABELS[item.status]}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions title="受評者快照" bordered size="small" column={2}>
            <Descriptions.Item label="法定負責人">
              {dash(item.legalRepresentative)}
            </Descriptions.Item>
            <Descriptions.Item label="聯絡電話">{dash(item.phone)}</Descriptions.Item>
            <Descriptions.Item label="位址" span={2}>
              {dash(item.registeredAddress)}
            </Descriptions.Item>
            <Descriptions.Item label="通訊位址" span={2}>
              {dash(item.mailingAddress)}
            </Descriptions.Item>
            <Descriptions.Item label="郵遞區號">{dash(item.postalCode)}</Descriptions.Item>
            <Descriptions.Item label="寄件地址">{dash(item.shippingAddress)}</Descriptions.Item>
            <Descriptions.Item label="客戶備註" span={2}>
              {dash(item.clientNote)}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions title="驗證資訊" bordered size="small" column={2}>
            <Descriptions.Item label="驗證標準" span={2}>
              {dash(item.verificationStandard)}
            </Descriptions.Item>
            <Descriptions.Item label="驗證行為">
              {dash(item.verificationBehavior)}
            </Descriptions.Item>
            <Descriptions.Item label="驗證類別">{dash(item.verificationType)}</Descriptions.Item>
            <Descriptions.Item label="驗證選項">{dash(item.verificationOption)}</Descriptions.Item>
            <Descriptions.Item label="有機/轉型期">{dash(item.organicStage)}</Descriptions.Item>
            <Descriptions.Item label="面積(公頃)">{dash(item.area)}</Descriptions.Item>
            <Descriptions.Item label="人數(農戶數)">{dash(item.memberCount)}</Descriptions.Item>
            <Descriptions.Item label="驗證範圍" span={2}>
              {dash(item.verificationScope)}
            </Descriptions.Item>
            <Descriptions.Item label="已完成驗證年度">
              {dash(item.completedYears)}
            </Descriptions.Item>
            <Descriptions.Item label="已完成驗證類型">
              {dash(item.completedTypes)}
            </Descriptions.Item>
            <Descriptions.Item label="通路">{dash(item.channels)}</Descriptions.Item>
            {/* 序126②③：客戶要求刪除「輔導員」（欄位保留於 DB，僅移除展示） */}
            {item.category === 'tap' ? (
              <>
                <Descriptions.Item label="產品類別">{dash(item.productCategory)}</Descriptions.Item>
                {/* 序126：TAP=驗證品項 / TOP=產品品項（名詞區分有機與產銷履歷） */}
                <Descriptions.Item label="驗證品項">{dash(item.productItems)}</Descriptions.Item>
              </>
            ) : (
              <Descriptions.Item label="產品品項" span={2}>
                {dash(item.verifiedItems)}
              </Descriptions.Item>
            )}
          </Descriptions>

          <Descriptions title="場所" bordered size="small" column={1}>
            <Descriptions.Item label="主要地號">{dash(item.primaryLandNo)}</Descriptions.Item>
            <Descriptions.Item label="驗證場所地址(主要地號)">
              {dash(item.mainParcelAddress)}
            </Descriptions.Item>
            <Descriptions.Item label="採收後處理場所">
              {dash(formatProcessingSites(item.processingSites))}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions title="發證/期限" bordered size="small" column={2}>
            <Descriptions.Item label="初次發證日期">{dash(item.firstIssuedAt)}</Descriptions.Item>
            <Descriptions.Item label="最新發證日期">{dash(item.latestIssuedAt)}</Descriptions.Item>
            <Descriptions.Item label="距上次發證(天)">
              {dash(daysSinceDate(item.latestIssuedAt))}
            </Descriptions.Item>
            <Descriptions.Item label="效期起算日">{dash(item.effectiveFrom)}</Descriptions.Item>
            <Descriptions.Item label="證書有效起">{dash(item.validFrom)}</Descriptions.Item>
            <Descriptions.Item label="證書有效迄">{dash(item.validUntil)}</Descriptions.Item>
          </Descriptions>

          <Descriptions title="進度/派案" bordered size="small" column={2}>
            <Descriptions.Item label="年度">{dash(item.currentYear)}</Descriptions.Item>
            <Descriptions.Item label="進度">{dash(item.currentStatus)}</Descriptions.Item>
            {([0, 1, 2, 3] as const).map((i) => (
              <Descriptions.Item key={i} label={auditorLabel(item.auditorYearBase, i)}>
                {dash(item[`auditorY${(i + 1) as 1 | 2 | 3 | 4}`])}
              </Descriptions.Item>
            ))}
            <Descriptions.Item label="連任3年預警">
              {item.rotationWarning ? <Tag color="red">連任3年</Tag> : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="預排次年度">
              {dash(item.plannedAuditorNext)}
            </Descriptions.Item>
            <Descriptions.Item label="稽核員迴避" span={2}>
              {dash(item.auditorAvoidance)}
            </Descriptions.Item>
            <Descriptions.Item label="派案注意事項" span={2}>
              {dash(item.dispatchNote)}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions title="結束/備註" bordered size="small" column={2}>
            <Descriptions.Item label="待停止/結束">{yn(item.pendingTermination)}</Descriptions.Item>
            <Descriptions.Item label="結束文件狀態">
              {dash(item.terminationDocStatus)}
            </Descriptions.Item>
            <Descriptions.Item label="結束追蹤備註" span={2}>
              {dash(item.terminationTrackingNote)}
            </Descriptions.Item>
            <Descriptions.Item label="紙本報告歸檔">
              {yn(item.paperReportArchived)}
            </Descriptions.Item>
            <Descriptions.Item label="欠費警示">{yn(item.paymentWarning)}</Descriptions.Item>
            <Descriptions.Item label="驗證組備註" span={2}>
              {dash(item.verificationGroupNote)}
            </Descriptions.Item>
            <Descriptions.Item label="管理組備註" span={2}>
              {dash(item.adminGroupNote)}
            </Descriptions.Item>
            <Descriptions.Item label="證書變更備註" span={2}>
              {dash(item.certificateChangeNote)}
            </Descriptions.Item>
          </Descriptions>

          {item.category === 'tap' ? (
            <Descriptions title="產銷履歷專屬" bordered size="small" column={2}>
              <Descriptions.Item label="L1上傳">{yn(item.l1Uploaded)}</Descriptions.Item>
              <Descriptions.Item label="計畫編號">{dash(item.planNo)}</Descriptions.Item>
              <Descriptions.Item label="產業別">{dash(item.industryType)}</Descriptions.Item>
              <Descriptions.Item label="產銷履歷編號">{dash(item.tapCode)}</Descriptions.Item>
              <Descriptions.Item label="標章使用" span={2}>
                {dash(item.labelUsage)}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Descriptions title="有機專屬" bordered size="small" column={2}>
              <Descriptions.Item label="環境補貼月份">
                {dash(item.environmentalSubsidyMonth)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Space>
      )}
    </Drawer>
  );
}
